import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";

interface LessonChatButtonProps {
  courseId: string;
  lessonId: string;
  lessonName: string;
  lessonContent: string;
  lessonDescription?: string;
  suggestedQuestions?: string[];
  hidden?: boolean;
  onClose?: () => void;
  onChatButtonClick?: () => void;
}

/**
 * Nút chat nổi hiển thị trong bài học, cho phép mở chat dialog toàn màn hình trên mobile
 */
const LessonChatButton: React.FC<LessonChatButtonProps> = ({
  courseId,
  lessonId,
  lessonName,
  lessonContent,
  lessonDescription = "",
  suggestedQuestions = [],
  hidden = false,
  onClose,
  onChatButtonClick,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");
  const [showAnimation, setShowAnimation] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  
  // Kiểm tra localStorage để xem người dùng đã từng mở chat trước đó chưa
  useEffect(() => {
    const chatOpenedBefore = localStorage.getItem(`chat_opened_${lessonId}`);
    if (chatOpenedBefore) {
      setHasOpened(true);
    }
  }, [lessonId]);

  // Hiệu ứng pulse animation sau khi component mount
  useEffect(() => {
    // Không kích hoạt hiệu ứng nữa
    setShowAnimation(false);
    // Không còn delay để hiển thị animation
    return () => {};
  }, []);
  
  // Ẩn nút khi hidden prop là true
  useEffect(() => {
    setIsVisible(!hidden);
  }, [hidden]);

  const handleChatButtonClick = () => {
    setShowAnimation(false);
    setHasOpened(true);
    
    // Lưu trạng thái đã mở vào localStorage
    localStorage.setItem(`chat_opened_${lessonId}`, 'true');
    
    // Gọi callback từ parent component
    if (onChatButtonClick) {
      onChatButtonClick();
    }
  };
  
  // Vị trí nút phù hợp với các thiết bị khác nhau
  const buttonPosition = useMemo(() => {
    if (isMobile) {
      return "bottom-32 right-4"; // Điều chỉnh cao hơn, tránh đè lên nút điều hướng
    } else if (isTablet) {
      return "bottom-36 right-6"; // Tablet - cao hơn một chút
    } else {
      return "bottom-32 right-8"; // Desktop - cao hơn, tránh đè lên nút điều hướng
    }
  }, [isMobile, isTablet]);

  if (!isVisible) return null;

  return (
    <div className={`fixed ${buttonPosition} z-40`}>
      {/* Nút chat chính */}
      <Button
        onClick={handleChatButtonClick}
        size="icon"
        className={`h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90 flex items-center justify-center transition-all duration-300 ease-in-out`}
        aria-label={t("common.chat_with_expert")}
      >
        <MessageSquare className="h-6 w-6" />
      </Button>
    </div>
  );
};

export default LessonChatButton; 